import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faExternalLink,
  faPlus,
  faMinus,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import Drawer from "@/components/ui/drawer";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import { apiClient } from "@/services/api";
import { format } from "date-fns";

type AmountUnitType = "units" | "value_product_currency" | "value_portfolio_currency";

interface TradeItem {
  type: "trade" | "offset";
  trade_id: string;
  date: string;
  action: string;
  amount_unit_type: AmountUnitType;
  description: string;
  units: number;
  product_value: number;
  value: number;
}

interface ReconciliationData {
  portfolio_id: string;
  resource_id: string;
  resource_name: string;
  as_at_date: string;
  starting_position: {
    date: string | null;
    units: number;
    product_value: number;
    value: number;
    source: string;
  };
  trades: TradeItem[];
  trade_summary: {
    direct_trades_count: number;
    offset_trades_count: number;
    total_trade_units: number;
    total_trade_product_value: number;
    total_trade_value: number;
    total_offset_units: number;
    total_offset_product_value: number;
    total_offset_value: number;
  };
  current_position: {
    date: string | null;
    units: number;
    product_value: number;
    value: number;
  };
  movement: {
    units: number;
    value: number;
    description: string;
  };
}

interface TradeReconciliationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  resourceId: string;
  resourceName: string;
  asAtDate?: string;
}

/**
 * Drawer component showing position reconciliation from MANUAL holdings through trades.
 */
export function TradeReconciliationDrawer({
  isOpen,
  onClose,
  portfolioId,
  resourceId,
  resourceName,
  asAtDate,
}: TradeReconciliationDrawerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReconciliationData | null>(null);
  const [reconcileMode, setReconcileMode] = useState<AmountUnitType>("units");

  useEffect(() => {
    if (isOpen && portfolioId && resourceId) {
      fetchReconciliation();
    }
  }, [isOpen, portfolioId, resourceId, asAtDate]);

  const fetchReconciliation = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (asAtDate) {
        params.append("as_at_date", asAtDate);
      }

      const response = await apiClient.get(
        `/trade-blotter/trades/reconciliation/${portfolioId}/${resourceId}?${params.toString()}`
      );

      setData(response.data);

      // Default reconcile mode based on the latest trade's input type
      if (response.data.trades.length > 0) {
        const latestTrade = response.data.trades[response.data.trades.length - 1];
        setReconcileMode(latestTrade.amount_unit_type || "units");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load reconciliation");
    } finally {
      setLoading(false);
    }
  };

  const getReconcileValue = (item: { units: number; product_value: number; value: number }) => {
    switch (reconcileMode) {
      case "units":
        return item.units;
      case "value_product_currency":
        return item.product_value;
      case "value_portfolio_currency":
        return item.value;
      default:
        return item.units;
    }
  };

  const getReconcileLabel = () => {
    switch (reconcileMode) {
      case "units":
        return "Units";
      case "value_product_currency":
        return "Product CCY";
      case "value_portfolio_currency":
        return "Portfolio CCY";
      default:
        return "Units";
    }
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const formatCurrency = (value: number) => {
    return `£${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), "dd MMM yyyy");
    } catch {
      return dateStr;
    }
  };

  const handleViewInTradeBlotter = () => {
    window.open(`/trade-blotter?portfolio_id=${portfolioId}`, "_blank");
  };

  const netTradeUnits = data
    ? data.trade_summary.total_trade_units + data.trade_summary.total_offset_units
    : 0;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Position Reconciliation"
      width="max-w-xl"
      headerActions={
        <Button
          size="sm"
          color="white"
          onClick={handleViewInTradeBlotter}
          className="flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faExternalLink} />
          Trade Blotter
        </Button>
      }
    >
      <div className="flex flex-col h-full">
        {/* Resource Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {resourceName || resourceId}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            As at {asAtDate ? formatDate(asAtDate) : "today"}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-2xl text-slate-400"
              />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
              <Button size="sm" color="white" onClick={fetchReconciliation} className="mt-4">
                Retry
              </Button>
            </div>
          ) : data ? (
            <div className="space-y-6">
              {/* Reconciliation Table */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600 dark:text-slate-300">
                        Description
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        Units
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        Product CCY
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        Portfolio CCY
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {/* Starting Position */}
                    <tr>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 dark:text-slate-300">
                            Starting position
                          </span>
                          <Badge color="blue" size="sm">
                            {data.starting_position.source}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {formatDate(data.starting_position.date)}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-slate-900 dark:text-white text-xs">
                        {formatNumber(data.starting_position.units)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-slate-900 dark:text-white text-xs">
                        {data.starting_position.product_value ? formatNumber(data.starting_position.product_value) : "-"}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-slate-900 dark:text-white text-xs">
                        {formatNumber(data.starting_position.value)}
                      </td>
                    </tr>

                    {/* Individual Trades */}
                    {data.trades.map((trade, index) => (
                      <tr key={trade.trade_id || index}>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={trade.units >= 0 ? faPlus : faMinus}
                              className={`text-xs ${
                                trade.units >= 0 ? "text-green-500" : "text-red-500"
                              }`}
                            />
                            <span className="text-slate-700 dark:text-slate-300 text-xs">
                              {trade.description}
                            </span>
                            {trade.type === "offset" && (
                              <Badge color="amber" size="sm">
                                Offset
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 ml-5">
                            {formatDate(trade.date)}
                          </div>
                        </td>
                        <td
                          className={`px-3 py-3 text-right font-mono text-xs ${
                            trade.units >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {trade.units >= 0 ? "+" : ""}
                          {formatNumber(trade.units)}
                        </td>
                        <td
                          className={`px-3 py-3 text-right font-mono text-xs ${
                            trade.product_value >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {trade.product_value ? (
                            <>
                              {trade.product_value >= 0 ? "+" : ""}
                              {formatNumber(trade.product_value)}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td
                          className={`px-3 py-3 text-right font-mono text-xs ${
                            trade.value >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {trade.value >= 0 ? "+" : ""}
                          {formatNumber(trade.value)}
                        </td>
                      </tr>
                    ))}

                    {data.trades.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-3 text-center text-slate-500 italic"
                        >
                          No trades affecting this position
                        </td>
                      </tr>
                    )}

                    {/* Current Position (Total) */}
                    <tr className="bg-indigo-50 dark:bg-indigo-900/30 font-semibold">
                      <td className="px-3 py-3">
                        <span className="text-indigo-700 dark:text-indigo-300">
                          Current position
                        </span>
                        <div className="text-xs text-indigo-600 dark:text-indigo-400 font-normal mt-0.5">
                          {formatDate(data.current_position.date)}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-indigo-700 dark:text-indigo-300 text-xs">
                        {formatNumber(data.current_position.units)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-indigo-700 dark:text-indigo-300 text-xs">
                        {data.current_position.product_value ? formatNumber(data.current_position.product_value) : "-"}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-indigo-700 dark:text-indigo-300 text-xs">
                        {formatNumber(data.current_position.value)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Reconciliation Summary */}
              <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-4 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-slate-700 dark:text-slate-300">
                    {getReconcileLabel()} Reconciliation
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <button
                      onClick={() => setReconcileMode("units")}
                      className={`px-2 py-1 rounded transition-colors ${
                        reconcileMode === "units"
                          ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium"
                          : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      Units
                    </button>
                    <button
                      onClick={() => setReconcileMode("value_product_currency")}
                      className={`px-2 py-1 rounded transition-colors ${
                        reconcileMode === "value_product_currency"
                          ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium"
                          : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      Product
                    </button>
                    <button
                      onClick={() => setReconcileMode("value_portfolio_currency")}
                      className={`px-2 py-1 rounded transition-colors ${
                        reconcileMode === "value_portfolio_currency"
                          ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-medium"
                          : "text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      Portfolio
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  How the current position was derived from MANUAL holdings and trades
                </p>
                <div className="space-y-1 text-slate-600 dark:text-slate-400 font-mono text-xs">
                  <div className="flex justify-between">
                    <span>Starting (MANUAL)</span>
                    <span>{formatNumber(getReconcileValue(data.starting_position))}</span>
                  </div>
                  {(() => {
                    const netTrades = data.trades.reduce(
                      (sum, t) => sum + getReconcileValue(t),
                      0
                    );
                    if (netTrades !== 0) {
                      return (
                        <div className="flex justify-between">
                          <span>+ Trades</span>
                          <span>
                            {netTrades >= 0 ? "+" : ""}
                            {formatNumber(netTrades)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {(() => {
                    const startVal = getReconcileValue(data.starting_position);
                    const netTrades = data.trades.reduce(
                      (sum, t) => sum + getReconcileValue(t),
                      0
                    );
                    const currentVal = getReconcileValue(data.current_position);
                    const expected = startVal + netTrades;
                    const difference = currentVal - expected;
                    if (Math.abs(difference) > 0.01) {
                      return (
                        <div className="flex justify-between text-purple-600 dark:text-purple-400">
                          <span>+ Market movement</span>
                          <span>
                            {difference >= 0 ? "+" : ""}
                            {formatNumber(difference)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="flex justify-between border-t border-slate-300 dark:border-slate-600 pt-1 mt-1 font-semibold text-slate-700 dark:text-slate-300">
                    <span>= Current position</span>
                    <span>{formatNumber(getReconcileValue(data.current_position))}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              No data available
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
